import {
  IWebsiteBuilderElementBase,
  IWebsiteBuilderElementDataBase,
  TWebsiteBuilderElement,
} from "./websiteBuilderElement";
import { websiteBuilderNormalizeHexColor, websiteBuilderNormalizeValue } from "../../schema/normalization/websiteBuilderNormalizeValue";
import { IWebsiteBuilderNodeDesignStyles } from "../websiteBuilderNode";
import { websiteBuilderGenerateDefaultHideableNodeVisibility } from "../websiteBuilderHideableNode";
import { IWebsiteBuilderSectionEntityStretch, websiteBuilderGenerateDefaultSectionEntityStretch } from "../websiteBuilderSectionEntity";
import { CSSProperties } from "react";

export const enum WebsiteBuilderDividerWeight {
  THIN = 1,
  REGULAR = 2,
  THICK = 5,
  EXTRA_THICK = 10,
}

function isWebsiteBuilderDividerWeight(value: number): value is WebsiteBuilderDividerWeight {
  return (
    value === (WebsiteBuilderDividerWeight.THIN as number) ||
    value === (WebsiteBuilderDividerWeight.REGULAR as number) ||
    value === (WebsiteBuilderDividerWeight.THICK as number) ||
    value === (WebsiteBuilderDividerWeight.EXTRA_THICK as number) 
  );
}

export type TWebsiteBuilderDividerStyle = "solid" | "dashed" | "dotted";

function isWebsiteBuilderDividerStyle(value: string): value is TWebsiteBuilderDividerStyle {
  return value === "solid" || value === "dashed" || value === "dotted";
}

export interface IWebsiteBuilderDividerSettings {
  weight: WebsiteBuilderDividerWeight;
  style: TWebsiteBuilderDividerStyle;
  color: string;
}

export interface IWebsiteBuilderDividerData extends IWebsiteBuilderElementDataBase {
  type: "divider";
  settings: IWebsiteBuilderDividerSettings;
  stretch: IWebsiteBuilderSectionEntityStretch;
}

export interface IWebsiteBuilderDivider extends IWebsiteBuilderElementBase {
  element: IWebsiteBuilderDividerData;
}

export function isWebsiteBuilderDivider(node: TWebsiteBuilderElement): node is IWebsiteBuilderDivider {
  return node?.element?.type === "divider";
}

function generateDefaultDividerSettings(): IWebsiteBuilderDividerSettings {
  return {
    weight: WebsiteBuilderDividerWeight.REGULAR,
    style: "solid",
    color: null,
  };
}

export function websiteBuilderGenerateDefaultDivider(): IWebsiteBuilderDivider {
  return {
    id: crypto.randomUUID(),
    type: "element",
    element: {
      type: "divider",
      layout: {
        desktop: {
          position: { x: 0, y: 0 },
          dimensions: { width: 600, height: WebsiteBuilderDividerWeight.EXTRA_THICK },
        },
        tablet: {
          position: { x: 0, y: 0 },
          dimensions: { width: 400, height: WebsiteBuilderDividerWeight.EXTRA_THICK },
        },
        mobile: {
          position: { x: 0, y: 0 },
          dimensions: { width: 200, height: WebsiteBuilderDividerWeight.EXTRA_THICK },
        },
      },
      visibility: websiteBuilderGenerateDefaultHideableNodeVisibility(),
      zIndex: 0,
      settings: generateDefaultDividerSettings(),
      stretch: websiteBuilderGenerateDefaultSectionEntityStretch(),
    },
  };
}

export function websiteBuilderNormalizeDividerSettings(partialSettings: Partial<IWebsiteBuilderDividerSettings>): IWebsiteBuilderDividerSettings {
  const defaultSettings = generateDefaultDividerSettings();

  if (partialSettings == null) {
    return defaultSettings;
  }

  const { weight, style, color } = partialSettings;

  const normalizedDividerSettings: IWebsiteBuilderDividerSettings = {
    weight: websiteBuilderNormalizeValue({
      type: "number",
      value: weight,
      defaultValue: defaultSettings.weight,
      required: true,
      validate: isWebsiteBuilderDividerWeight,
    }) as WebsiteBuilderDividerWeight,
    style: websiteBuilderNormalizeValue({
      type: "string",
      value: style,
      defaultValue: defaultSettings.style,
      required: true,
      validate: isWebsiteBuilderDividerStyle,
    }) as TWebsiteBuilderDividerStyle,
    color: websiteBuilderNormalizeHexColor(color, defaultSettings.color, false),
  };

  return normalizedDividerSettings;
}

export function websiteBuilderGenerateDividerDesignStyles(
  settings: IWebsiteBuilderDividerSettings,
): IWebsiteBuilderNodeDesignStyles {
  const styleProperties: CSSProperties = {
    borderTopWidth: `${settings.weight}px`,
    borderTopStyle: settings.style,
    borderTopColor: settings.color,
  };

  let styleDeclaration = `border-top-width: ${styleProperties.borderTopWidth}; border-top-style: ${styleProperties.borderTopStyle};`;

  if (styleProperties.borderTopColor) {
    styleDeclaration += `border-top-color: ${styleProperties.borderTopColor};`;
  }

  return {
    className: "website-builder-divider",
    styleProperties,
    styleDeclaration,
  };
}
